import React, { useState, Suspense } from 'react'
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle'
import Flag from '@/Components/Elements/Flag'

import { UpperMenuLocalesProps, Locale } from '../model/types'
import {
    getLanguageCodeFromCode,
    getFlagCodeFromLocale,
    isBrowserLocaleAvailable,
    isStoredLocaleAvailable,
} from '../model/locale.utils'
import { useLocales } from '../hooks/useLocales'

export const UpperMenuLocales: React.FC<UpperMenuLocalesProps> = ({ kedo }) => {
    const [browserLang] = useState<string>(kedo.env().getBrowserLanguage())
    const [newLang, setNewLang] = useState<boolean>(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    useLocales(kedo)

    const getCurrentLocale = () => {
        const currentLocaleCode = kedo.env().locale.code
        return (
            <Flag
                code={getFlagCodeFromLocale(currentLocaleCode)}
                height="16"
                width="16"
            />
        )
    }

    const getAvailableLocales = () => {
        const availableLocales = kedo.env().getEnvironmentLocales()

        return availableLocales.map((locale: Locale) => {
            const flagCode = getFlagCodeFromLocale(locale.code)
            const localeCode = getLanguageCodeFromCode(locale.code)

            return (
                <MenuItem
                    key={flagCode}
                    onClick={() => {
                        kedo.env().setLocale(locale)
                        kedo.utils().getI18N().changeLanguage(localeCode)
                        setAnchorEl(null)
                    }}
                >
                    <Flag code={flagCode} />
                    <Typography variant="body2">
                        &nbsp;&nbsp; {locale.name}
                    </Typography>
                </MenuItem>
            )
        })
    }

    const getPopUp = () => {
        const availableLocales = kedo.env().getEnvironmentLocales()
        const selectedLocale = kedo.env().getSelectedLocale()

        if (!availableLocales) return null

        const checkLocale = isBrowserLocaleAvailable(
            availableLocales,
            browserLang
        )

        const checkLocalStorage = isStoredLocaleAvailable(
            availableLocales,
            selectedLocale
        )

        if (!checkLocale && !checkLocalStorage) {
            return (
                <Box
                    sx={{
                        p: 2,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faExclamationCircle}
                            style={{ fontSize: 18 }}
                        />
                        &nbsp; {kedo.t('Language not available')}
                    </Typography>

                    <Typography sx={{ mt: 1 }}>
                        {kedo.t('Locales_warning')}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {kedo.t('Available languages')}:
                        </Typography>

                        {availableLocales.map((locale: Locale) => {
                            const localeCode = getLanguageCodeFromCode(
                                locale.code
                            )

                            return (
                                <Typography
                                    variant="body2"
                                    key={locale.id}
                                    onClick={() => {
                                        kedo.env().setLocale(locale)
                                        kedo.utils()
                                            .getI18N()
                                            .changeLanguage(localeCode)
                                        setNewLang(true)
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        mb: 1,
                                        pl: 1,
                                    }}
                                >
                                    {locale.name}
                                </Typography>
                            )
                        })}
                    </Box>
                </Box>
            )
        }

        return null
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            {newLang ? null : getPopUp()}

            {kedo.env().getEnvironmentLocales() &&
            kedo.env().getEnvironmentLocales().length > 1 ? (
                <Suspense fallback={<LoadingDefault />}>
                    <IconButton
                        size="large"
                        color="inherit"
                        onClick={handleMenuOpen}
                    >
                        {kedo.env().locale ? getCurrentLocale() : '?'}
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        {getAvailableLocales()}
                    </Menu>
                </Suspense>
            ) : null}
        </>
    )
}
